
import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Sidebar from '../../components/feature/Sidebar';
import { supabase, BlogPost } from '../../lib/supabase';

export default function BlogPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('全て');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const articlesPerPage = 8;

  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    const categoryFromUrl = searchParams.get('category');
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
      setCurrentPage(1);
    }
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
      setCurrentPage(1);
    } else if (!searchFromUrl) {
      setSelectedCategory('全て');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .order('published_date', { ascending: false });
      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error) {
      console.error('ブログ記事の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = useMemo(() => {
    return blogPosts.filter((article) => {
      const matchesCategory = selectedCategory === '全て' || article.category === selectedCategory;
      const matchesSearch =
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [blogPosts, selectedCategory, searchQuery]);

  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * articlesPerPage,
    currentPage * articlesPerPage,
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .replace(/\//g, '.');
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* パンくずリスト */}
      <div className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <nav className="flex items-center gap-2 text-xs text-gray-500">
            <Link to="/" className="hover:text-cyan-600 transition-colors cursor-pointer">
              ホーム
            </Link>
            <span>&gt;</span>
            <span className="text-gray-800">ブログ</span>
            {selectedCategory !== '全て' && (
              <>
                <span>&gt;</span>
                <span className="text-gray-800">{selectedCategory}</span>
              </>
            )}
            {searchQuery && (
              <>
                <span>&gt;</span>
                <span className="text-gray-800">「{searchQuery}」の検索結果</span>
              </>
            )}
          </nav>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-12">
          {/* メインコンテンツ */}
          <div className="flex-1 min-w-0">
            {/* 検索中・カテゴリー選択中の表示 */}
            {(searchQuery || selectedCategory !== '全て') && (
              <div className="mb-8 flex items-center justify-between">
                <div>
                  {searchQuery && (
                    <p className="text-sm text-gray-600">
                      「<strong className="text-gray-900">{searchQuery}</strong>」の検索結果
                      <span className="ml-2 text-gray-400">({filteredArticles.length}件)</span>
                    </p>
                  )}
                  {selectedCategory !== '全て' && !searchQuery && (
                    <p className="text-sm text-gray-600">
                      カテゴリー：<strong className="text-gray-900">{selectedCategory}</strong>
                      <span className="ml-2 text-gray-400">({filteredArticles.length}件)</span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('全て');
                    setCurrentPage(1);
                    navigate('/blog');
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-close-line"></i>
                  クリア
                </button>
              </div>
            )}

            {/* ローディング */}
            {loading ? (
              <div className="space-y-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex gap-6 animate-pulse border-b border-gray-100 pb-6">
                    <div className="w-64 h-40 bg-gray-100 rounded flex-shrink-0"></div>
                    <div className="flex-1 space-y-3 py-2">
                      <div className="flex gap-3">
                        <div className="h-4 bg-gray-100 rounded w-20"></div>
                        <div className="h-4 bg-gray-100 rounded w-16"></div>
                      </div>
                      <div className="h-5 bg-gray-100 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-100 rounded w-full"></div>
                      <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : paginatedArticles.length > 0 ? (
              <>
                {/* 記事一覧 */}
                <div className="space-y-0">
                  {paginatedArticles.map((article, index) => (
                    <article
                      key={article.id}
                      className={`group ${index < paginatedArticles.length - 1 ? 'border-b border-gray-150' : ''}`}
                    >
                      <Link
                        to={`/blog/${article.slug}`}
                        className="flex gap-6 py-7 cursor-pointer"
                      >
                        {/* サムネイル */}
                        <div className="w-60 h-36 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>

                        {/* テキスト部分 */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                          <div>
                            {/* 日付・カテゴリー */}
                            <div className="flex items-center gap-3 mb-2.5">
                              <time className="text-sm text-gray-500 tabular-nums">
                                {formatDate(article.published_date)}
                              </time>
                              <span className="text-xs text-gray-600 bg-gray-100 px-2.5 py-0.5 rounded">
                                {article.category}
                              </span>
                              {article.video_url && (
                                <span className="text-xs text-red-500 flex items-center gap-1">
                                  <i className="ri-play-circle-line"></i>
                                  動画あり
                                </span>
                              )}
                            </div>

                            {/* タイトル */}
                            <h2 className="text-base font-bold text-gray-900 leading-relaxed mb-2 group-hover:text-cyan-600 transition-colors line-clamp-2">
                              {article.title}
                            </h2>

                            {/* 抜粋 */}
                            <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                              {article.excerpt}
                            </p>
                          </div>

                          {/* 著者 */}
                          <div className="flex items-center gap-2 mt-3">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                              <i className="ri-user-line text-xs text-gray-400"></i>
                            </div>
                            <span className="text-xs text-gray-500">
                              {article.author || '編集部'}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>

                {/* ページネーション */}
                {totalPages > 1 && (
                  <nav className="flex items-center justify-center gap-1 mt-12 pt-8 border-t border-gray-100">
                    {getPageNumbers().map((page, idx) =>
                      page === '...' ? (
                        <span key={`dots-${idx}`} className="w-10 h-10 flex items-center justify-center text-gray-400 text-sm">
                          …
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => {
                            setCurrentPage(page as number);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                            currentPage === page
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      ),
                    )}
                    {currentPage < totalPages && (
                      <button
                        onClick={() => {
                          setCurrentPage((prev) => prev + 1);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="ml-2 px-4 h-10 flex items-center justify-center text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      >
                        NEXT
                      </button>
                    )}
                  </nav>
                )}
              </>
            ) : (
              <div className="py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
                  <i className="ri-article-line text-5xl"></i>
                </div>
                <p className="text-gray-500 text-sm">該当する記事が見つかりませんでした</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('全て');
                    setCurrentPage(1);
                    navigate('/blog');
                  }}
                  className="mt-4 text-sm text-cyan-600 hover:text-cyan-700 cursor-pointer"
                >
                  すべての記事を表示
                </button>
              </div>
            )}
          </div>

          {/* サイドバー */}
          <Sidebar />
        </div>
      </main>

      {/* SNS・お問い合わせバー */}
      <section className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">フォローしていただけると嬉しいです！</span>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors cursor-pointer" rel="nofollow">
                <i className="ri-twitter-x-line text-sm"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors cursor-pointer" rel="nofollow">
                <i className="ri-youtube-line text-sm"></i>
              </a>
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-gray-800 transition-colors cursor-pointer text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-customer-service-2-line"></i>
            ご相談・お問い合わせはこちら
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
